import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, MessageCircle, Sparkles, TrendingDown, Timer, RotateCcw } from "lucide-react";
import { buildWhatsAppLink } from "@/lib/constants";

/**
 * 60-Sekunden Spar-Check
 * Vergleicht Leasinggeber-Nachzahlung vs. AutoCoach-Preis
 * 4 Fragen → Ergebnis mit Ersparnis + WhatsApp CTA
 */

const QUESTIONS = [
  {
    id: "scratches",
    label: "Lackkratzer & Steinschläge",
    hint: "Kleine Kratzer auf Türen, Stoßstangen, Kotflügel etc.",
    options: [
      { value: "keine",  label: "Keine sichtbaren",  dealer: 0,    coach: 0 },
      { value: "leicht", label: "1–3 leichte Stellen", dealer: 420,  coach: 150 },
      { value: "mittel", label: "4–8 Kratzer",         dealer: 980,  coach: 340 },
      { value: "stark",  label: "Viele / tief",        dealer: 1850, coach: 620 },
    ],
  },
  {
    id: "dents",
    label: "Dellen & Beulen",
    hint: "Parkrempler, Hagel oder kleine Beulen",
    options: [
      { value: "keine",   label: "Keine",                dealer: 0,    coach: 0 },
      { value: "klein",   label: "1–2 kleine Dellen",   dealer: 480,  coach: 190 },
      { value: "mehrere", label: "Mehrere Dellen",       dealer: 1180, coach: 460 },
      { value: "grosse",  label: "Größere Beulen",       dealer: 2200, coach: 880 },
    ],
  },
  {
    id: "interior",
    label: "Innenraum-Zustand",
    hint: "Polster, Leder, Himmel, Teppich",
    options: [
      { value: "top",     label: "Top-Zustand",                   dealer: 0,   coach: 0 },
      { value: "leicht",  label: "Leichte Gebrauchsspuren",       dealer: 240, coach: 95 },
      { value: "stark",   label: "Starke Spuren",                 dealer: 520, coach: 210 },
      { value: "flecken", label: "Flecken oder Geruch",           dealer: 880, coach: 310 },
    ],
  },
  {
    id: "exterior",
    label: "Außenaufbereitung",
    hint: "Wie viel Pflege braucht der Lack vor der Rückgabe?",
    options: [
      { value: "sauber",    label: "Gerade gewaschen",     dealer: 0,   coach: 0 },
      { value: "normal",    label: "Standard-Aufbereitung", dealer: 220, coach: 110 },
      { value: "voll",      label: "Vollaufbereitung & Politur", dealer: 480, coach: 230 },
    ],
  },
];

const fmt = (n) => n.toLocaleString("de-DE") + " €";

const Step = ({ q, value, onChange, onNext, onBack, isFirst, isLast, index, total }) => (
  <motion.div
    key={q.id}
    initial={{ opacity: 0, x: 40 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -40 }}
    transition={{ duration: 0.35 }}
    className="w-full"
  >
    <div className="flex items-center justify-between mb-6">
      <p className="text-sm font-semibold text-[#1FA3B5]">Frage {index + 1} / {total}</p>
      <p className="text-sm text-slate-500">60 Sek. Spar-Check</p>
    </div>

    <h3 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 leading-tight">
      {q.label}
    </h3>
    <p className="mt-2 text-slate-500 text-xs sm:text-sm">{q.hint}</p>

    <div className="mt-6 sm:mt-8 grid sm:grid-cols-2 gap-2.5 sm:gap-3">
      {q.options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => onChange(opt.value)}
            data-testid={`check-option-${q.id}-${opt.value}`}
            className={`text-left px-4 sm:px-5 py-3.5 sm:py-4 rounded-xl border-2 transition-all ${
              selected
                ? "border-[#1FA3B5] bg-[#E6F6F8] text-slate-900"
                : "border-slate-200 hover:border-[#1FA3B5]/50 hover:bg-slate-50 text-slate-700"
            }`}
          >
            <span className="font-medium">{opt.label}</span>
          </button>
        );
      })}
    </div>

    <div className="mt-8 sm:mt-10 flex items-center justify-between gap-3">
      <button
        type="button"
        onClick={onBack}
        disabled={isFirst}
        data-testid="check-back-button"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Zurück
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={!value}
        data-testid="check-next-button"
        className="inline-flex items-center gap-2 bg-[#1FA3B5] text-white rounded-full px-7 py-3 font-semibold hover:bg-[#178A9A] disabled:bg-slate-300 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5 shadow-lg shadow-[#1FA3B5]/25"
      >
        {isLast ? "Ergebnis anzeigen" : "Weiter"} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </motion.div>
);

const LeasingCheck = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const totals = useMemo(() => {
    let dealer = 0;
    QUESTIONS.forEach((q) => {
      const opt = q.options.find((o) => o.value === answers[q.id]);
      if (opt) {
        dealer += opt.dealer;
      }
    });
    // Mit AutoCoach zahlen Sie 0 € Nachzahlung — wir kümmern uns um alles.
    const coach = 0;
    return { dealer, coach, save: dealer };
  }, [answers]);

  const setAnswer = (val) => setAnswers((s) => ({ ...s, [QUESTIONS[step].id]: val }));

  const next = () => {
    if (step < QUESTIONS.length - 1) setStep((s) => s + 1);
    else setDone(true);
  };
  const back = () => setStep((s) => Math.max(0, s - 1));
  const reset = () => { setAnswers({}); setStep(0); setDone(false); };

  const waMessage = `Hallo AutoCoach, ich habe den 60-Sekunden Spar-Check gemacht.

Mein voraussichtlicher Nachzahlungs-Wert beim Leasinggeber: ${fmt(totals.dealer)}
Mit AutoCoach: 0 € Nachzahlung
Mögliche Ersparnis: ${fmt(totals.save)}

Bitte melden Sie sich für ein unverbindliches Angebot.`;

  return (
    <section id="spar-check" data-testid="leasing-check-section" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-[#E6F6F8] rounded-full blur-3xl opacity-70 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1FA3B5] text-white text-sm font-semibold mb-5 shadow-lg shadow-[#1FA3B5]/30">
            <Timer className="w-4 h-4" /> 60-Sekunden Spar-Check
          </div>
          <h2 className="font-heading text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
            Wieviel hätte Ihre Leasingrückgabe<br />
            <span className="text-[#1FA3B5]">Sie wirklich gekostet?</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600">
            Beantworten Sie 4 kurze Fragen. Sie sehen sofort, was der Leasinggeber Ihnen
            in Rechnung stellen würde – und was Sie mit AutoCoach sparen.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-12 sm:mt-14 bg-white rounded-2xl sm:rounded-[2rem] border border-slate-200 shadow-2xl shadow-slate-900/5 p-5 sm:p-8 lg:p-14 max-w-3xl mx-auto"
          data-testid="leasing-check-card"
        >
          {/* Progress bar */}
          {!done && (
            <div className="mb-10">
              <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-[#1FA3B5]"
                  initial={{ width: 0 }}
                  animate={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {!done ? (
              <Step
                q={QUESTIONS[step]}
                value={answers[QUESTIONS[step].id]}
                onChange={setAnswer}
                onNext={next}
                onBack={back}
                isFirst={step === 0}
                isLast={step === QUESTIONS.length - 1}
                index={step}
                total={QUESTIONS.length}
              />
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                data-testid="check-result"
                className="text-center"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E6F6F8] text-[#0F7783] text-sm font-semibold mb-6">
                  <Sparkles className="w-4 h-4" /> Ihr persönliches Ergebnis
                </div>

                <h3 className="font-heading text-3xl lg:text-4xl font-bold text-slate-900">
                  Sie könnten <span className="text-[#1FA3B5]">{fmt(totals.save)}</span> sparen.
                </h3>
                <p className="mt-4 text-slate-600">
                  Hochrechnung basiert auf typischen Leasinggeber-Preisen vs. AutoCoach-Festpreisen.
                </p>

                <div className="mt-10 grid sm:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-red-100 bg-red-50/50 p-6 text-left" data-testid="result-dealer">
                    <p className="text-xs uppercase tracking-wider font-semibold text-red-600">Beim Leasinggeber</p>
                    <p className="font-heading text-3xl font-bold text-slate-900 mt-2 line-through decoration-red-400 decoration-2">
                      {fmt(totals.dealer)}
                    </p>
                    <p className="text-sm text-slate-500 mt-2">Typische Nachzahlung</p>
                  </div>
                  <div className="rounded-2xl border-2 border-[#1FA3B5] bg-gradient-to-br from-[#E6F6F8] to-white p-6 text-left relative overflow-hidden" data-testid="result-coach">
                    {/* Pulsing accent */}
                    <span className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-[#1FA3B5]/20 blur-2xl" />
                    <p className="text-xs uppercase tracking-wider font-semibold text-[#0F7783] relative">Mit AutoCoach</p>
                    <p className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1FA3B5] mt-2 relative">0 €</p>
                    <p className="text-sm text-slate-700 mt-2 font-medium relative">Keine Nachzahlung. Garantiert.</p>
                  </div>
                </div>

                <div className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-slate-900 text-white font-semibold" data-testid="result-savings">
                  <TrendingDown className="w-5 h-5 text-[#5DD8E5]" />
                  Ihre Ersparnis: {fmt(totals.save)}
                </div>

                <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href={buildWhatsAppLink(waMessage)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="check-result-whatsapp"
                    className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1DAE54] text-white rounded-full px-8 py-4 font-semibold transition-all hover:-translate-y-0.5 shadow-xl shadow-[#25D366]/30"
                  >
                    <MessageCircle className="w-5 h-5" /> Jetzt Festpreis anfragen
                  </a>
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="check-reset"
                    className="inline-flex items-center justify-center gap-2 bg-white text-slate-700 border border-slate-200 rounded-full px-8 py-4 font-semibold hover:bg-slate-50 transition-all"
                  >
                    <RotateCcw className="w-4 h-4" /> Check neu starten
                  </button>
                </div>

                <p className="mt-8 text-xs text-slate-400 max-w-xl mx-auto">
                  Die Werte sind eine unverbindliche Schätzung auf Basis branchenüblicher Preise. Ihr individuelles Festpreis-Angebot
                  erhalten Sie nach kurzer Foto-Prüfung per WhatsApp.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default LeasingCheck;
